import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ApiServices from "../../Services/ApiServices/ApiServices";

const ReferList = () => {
  const userData = JSON.parse(sessionStorage.getItem("user"));
  const referralSourceId = userData?.id;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  /* ---------------- Fetch Leads ---------------- */
  const fetchLeads = async () => {
    if (!referralSourceId) {
      setSnack({
        open: true,
        message: "Session expired. Please login again.",
        severity: "error",
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await ApiServices.getwitParams(
        `/referral-leads/?referral_source_id=${referralSourceId}`
      );

      const data = Array.isArray(res?.data) ? res.data : [];
      setLeads(
        data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      );
    } catch {
      setLeads([]);
      setSnack({
        open: true,
        message: "Failed to load referral leads",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  /* ---------------- Search ---------------- */
  const filteredLeads = useMemo(() => {
    if (!search) return leads;
    return leads.filter((l) =>
      l?.customer_name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [leads, search]);

  return (
    <Box sx={{  p: { xs: 2, md: 4 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 6 }}>
        {/* HEADER */}
        <Typography variant="h6" fontWeight={600}>
          My Referred Leads
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Track customers you have referred to Rite Home Pros.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* SEARCH */}
        <TextField
          fullWidth
          placeholder="Search by customer name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 3 }}
        />

        {/* LOADING */}
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
            <Typography mt={2}>Loading leads...</Typography>
          </Box>
        ) : (
          <>
            {/* ================= DESKTOP TABLE ================= */}
            {!isMobile && (
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                    <TableCell><b>#</b></TableCell>
                    <TableCell><b>Customer</b></TableCell>
                    <TableCell><b>Phone</b></TableCell>
                    <TableCell><b>Address</b></TableCell>
                    <TableCell><b>Service</b></TableCell>
                    <TableCell><b>Notes</b></TableCell>
                    <TableCell><b>Converted</b></TableCell>
                    <TableCell><b>Payment</b></TableCell>
                    <TableCell><b>Date</b></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredLeads.map((lead, index) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>{index + 1}</TableCell>

                      <TableCell>{lead.customer_name || "-"}</TableCell>

                      <TableCell>{lead.customer_phone || "-"}</TableCell>

                      <TableCell>
                        {lead.address || "-"}, {lead.zip_code || ""}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                        {lead.service_needed || "-"}
                      </TableCell>

                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                        >
                          {lead.notes || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={lead.is_converted ? "Yes" : "No"}
                          color={lead.is_converted ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={lead.payment_status || "NOT_PAID"}
                          color={
                            lead.payment_status === "PAID"
                              ? "success"
                              : lead.payment_status === "PARTIAL"
                              ? "warning"
                              : "default"
                          }
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        {new Date(lead.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}

                  {filteredLeads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No leads found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            {/* ================= MOBILE CARD VIEW ================= */}
            {isMobile && (
              <Box display="flex" flexDirection="column" gap={2}>
                {filteredLeads.map((lead, index) => (
                  <Paper
                    key={lead.id}
                    sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}
                  >
                    <Typography fontWeight={600}>
                      #{index + 1} {lead.customer_name}
                    </Typography>

                    <Typography variant="body2">
                      📞 {lead.customer_phone}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      📍 {lead.address || "-"}, {lead.zip_code}
                    </Typography>

                    <Typography mt={1} fontWeight={600} color="primary.main">
                      {lead.service_needed}
                    </Typography>

                    {/* NOTES */}
                    {lead.notes && (
                      <Box mt={1}>
                        <Typography variant="caption" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {lead.notes}
                        </Typography>
                      </Box>
                    )}

                    <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={
                          lead.is_converted ? "Converted to Job" : "Not Converted"
                        }
                        color={lead.is_converted ? "success" : "default"}
                        size="small"
                      />

                      <Chip
                        label={lead.payment_status}
                        size="small"
                        color={
                          lead.payment_status === "PAID"
                            ? "success"
                            : lead.payment_status === "PARTIAL"
                            ? "warning"
                            : "default"
                        }
                      />
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                    >
                      {new Date(lead.created_at).toLocaleDateString()}
                    </Typography>
                  </Paper>
                ))}

                {filteredLeads.length === 0 && (
                  <Typography textAlign="center" color="text.secondary">
                    No leads found
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferList;
